import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';

/**
 * Keyboard shortcuts. A shortcut is a key ("r", "/", "?", "Enter") or "mod+<key>" (⌘ on macOS,
 * Ctrl elsewhere). Single keys never fire while the user types in a field, and page shortcuts
 * pause while a dialog or popover is open, unless the control they belong to is inside it.
 */

/** Heading of a shortcut in the shortcuts dialog: the whole console, lists, or the current page. */
export type ShortcutGroup = 'general' | 'lists' | 'page';

export interface Shortcut {
  keys: string;
  /** Already translated. */
  description: string;
  group: ShortcutGroup;
}

interface Registry {
  register(id: string, shortcut: Shortcut): () => void;
  shortcuts: Shortcut[];
}

const RegistryContext = createContext<Registry | null>(null);

/** Collects the shortcuts mounted components declare, for the `?` dialog. Mounted in UiProvider. */
export function ShortcutsProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Map<string, Shortcut>>(() => new Map());
  const register = useCallback((id: string, shortcut: Shortcut) => {
    setEntries((prev) => new Map(prev).set(id, shortcut));
    return () =>
      setEntries((prev) => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
  }, []);
  const value = useMemo<Registry>(() => {
    // one row per key: the same shortcut declared twice (e.g. two buttons) is listed once
    const byKey = new Map<string, Shortcut>();
    entries.forEach((s) => byKey.set(`${s.group}:${s.keys}`, s));
    return { register, shortcuts: [...byKey.values()] };
  }, [entries, register]);
  return <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
}

/** The shortcuts currently available, for the shortcuts dialog. */
export function useShortcutList(): Shortcut[] {
  return useContext(RegistryContext)?.shortcuts ?? [];
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Whether the user is typing: shortcuts made of a single key must not fire. */
function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

function matches(keys: string, e: KeyboardEvent): boolean {
  const parts = keys.toLowerCase().split('+');
  const key = parts.at(-1);
  const mod = parts.includes('mod');
  if (mod !== (isMac ? e.metaKey : e.ctrlKey)) return false;
  if (e.altKey || (!mod && (e.metaKey || e.ctrlKey))) return false;
  return e.key.toLowerCase() === key;
}

/** An open modal layer (Radix dialog, alert dialog or popover). */
function openLayer(): Element | null {
  return document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
}

export interface HotkeyOptions {
  /** Listed in the shortcuts dialog when given (already translated). */
  description?: string;
  /** Defaults to `page`. */
  group?: ShortcutGroup;
  enabled?: boolean;
  /** The element the shortcut acts on: it keeps working while a dialog containing it is open. */
  scope?: RefObject<HTMLElement | null>;
  /** Fire while focus is in a text field (only sensible for mod+ shortcuts). */
  allowInInputs?: boolean;
  /** Extra condition checked on each press; when false the key is left alone (not prevented). */
  when?(e: KeyboardEvent): boolean;
}

/** Runs `handler` when `keys` is pressed (see the module comment for the rules). */
export function useHotkey(keys: string | null | undefined, handler: (e: KeyboardEvent) => void, options: HotkeyOptions = {}) {
  const { description, group = 'page', enabled = true, scope, allowInInputs } = options;
  const handlerRef = useRef(handler);
  const whenRef = useRef(options.when);
  useEffect(() => {
    handlerRef.current = handler;
    whenRef.current = options.when;
  });
  const registry = useContext(RegistryContext);
  const register = registry?.register;
  const id = useId();

  useEffect(() => {
    if (!keys || !enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.repeat || !matches(keys, e)) return;
      if (!allowInInputs && typing(e.target)) return;
      const layer = openLayer();
      if (layer && !(scope?.current && layer.contains(scope.current))) return;
      if (whenRef.current && !whenRef.current(e)) return;
      e.preventDefault();
      handlerRef.current(e);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [keys, enabled, scope, allowInInputs]);

  useEffect(() => {
    if (!keys || !enabled || !description || !register) return;
    return register(id, { keys, description, group });
  }, [keys, enabled, description, group, register, id]);
}

/** Display parts of a shortcut: "mod+k" → ["⌘", "K"] on macOS, ["Ctrl", "K"] elsewhere. */
export function keyLabels(keys: string): string[] {
  return keys.split('+').map((k) => {
    if (k === 'mod') return isMac ? '⌘' : 'Ctrl';
    if (k === 'Enter') return '↵';
    if (k === 'Escape') return 'Esc';
    return k.length === 1 ? k.toUpperCase() : k;
  });
}
